import { useId, useState } from 'react';
import { Badge, chakra, Grid, HStack, Icon, Separator, Stack, StackSeparator, Text, VStack } from '@chakra-ui/react';
import { LuClipboardList, LuFileText, LuInfo, LuListChecks, LuTriangleAlert } from 'react-icons/lu';
import type { PlanNodeOut, SourceRefOut } from '@/client';
import type { DismissDetail } from '@/components/maintenance/review/reviewQueueState';
import ProposalVerdictActions from '@/components/maintenance/review/ProposalVerdictActions';
import { UNDATED_VERDICT_GUIDANCE, VERDICT_GUIDANCE } from '@/components/maintenance/review/KeepOnScheduleButton';
import { canElectToSchedule } from '@/components/maintenance/model/planReviewFilter';
import type { PlanPlacement } from '@/components/maintenance/model/planVocabulary';
import { DismissReasonForm } from '@/components/common/review/DismissReasonMenu';
import { PLAN_DISMISS_VOCABULARY } from '@/components/maintenance/review/planDismissVocabulary';
import {
	groupActionLabel,
	subtaskCount,
	procedureChildren,
	taskChildren,
	withChildren,
} from '@/components/maintenance/model/planSelectors';
import { dismissActionFor } from '@/components/maintenance/review/reviewQueueState';
import ProposalFrequency from '@/components/maintenance/review/ProposalFrequency';
import ReviewFact from '@/components/maintenance/review/ReviewFact';
import { PlanActions } from '@/components/maintenance/model/planVocabulary';
import { planTypeLabel } from '@/components/maintenance/model/planCategoryLabels';
import { visiblePlanSourceRefs } from '@/components/maintenance/model/planNodeFacts';

/** Given the record id a source names and every page cited for it. */
export type SelectSource = (docId: number, pages?: number[]) => void;

export interface ProposalCardProps {
	node: PlanNodeOut;
	/** Given the surface the household elected for it. */
	onKeep: (placement: PlanPlacement) => void;
	/**
	 * A cadence the household corrected here, which rides on the keep rather than going as a second
	 * write. Absent where the card cannot be rescheduled, which leaves the frequency as plain text.
	 */
	cadence?: string;
	onCadenceChange?: (cadence: string | undefined) => void;
	/** A corrected first-due date, which rides on the same keep as the cadence. */
	anchorDate?: string;
	onAnchorDateChange?: (anchorDate: string | undefined) => void;
	/** Absent where the card advertises no way to be turned down, in which case no control is drawn. */
	/** Given the dismissal reason and any explanation required by it. */
	onDismiss?: (detail?: DismissDetail) => void;
	/** This card's verdict is in flight. Both controls lock, so one card cannot be answered twice. */
	isPending?: boolean;
	/** The last verdict was refused. The controls stay live, and pressing again retries it. */
	hasFailed?: boolean;
	/** Opens the full card. Absent where there is nowhere to open — a story, or a surface with no drawer. */
	onOpenDetails?: () => void;
	/**
	 * Withhold the verdicts, the surface having somewhere better to put them.
	 *
	 * The workspace layout keeps a detail pane open beside this list, and its footer carries the same
	 * two buttons. Drawing them here as well would put two sets of one decision on screen at once.
	 */
	hideVerdicts?: boolean;
	/** This is the card the pane beside the list is showing. */
	isSelected?: boolean;
	/**
	 * The sub-tasks are being drawn as cards of their own beneath this one, so listing their titles
	 * here would say the same thing twice. Steps are unaffected — nothing else draws those.
	 */
	hideChildList?: boolean;
	/**
	 * Drop the card's own frame, for a surface that already is one.
	 *
	 * A dialog draws a border, a fill and a padding of its own, so repeating them inside reads as a
	 * card in a box and costs the width a narrow screen has least of.
	 */
	flush?: boolean;
	/**
	 * Whether to draw the review evidence beneath the card's stable summary.
	 *
	 * Archive uses this to disclose the original review card in place: the card does not turn into a
	 * different row when collapsed, it simply withholds the facts until they are requested.
	 */
	showDetails?: boolean;
	/** Opens the record a cited source names, at the page it was cited on. */
	onSelectSource?: SelectSource;
	/**
	 * Document ids returned by the visibility-filtered Records list. An empty set hides every source.
	 *
	 * Required rather than optional, and deliberately awkward for a caller with nothing to pass: the
	 * plan's provenance is not visibility-filtered, so a card handed no set would name files the
	 * reader may have no right to. A caller that genuinely has no list passes an empty set and shows
	 * nothing. See docs/schedule/source-visibility.md.
	 */
	visibleDocumentIds: ReadonlySet<number>;
	/**
	 * The group this proposal was taken out of, where it was.
	 *
	 * Individual review asks about a group's tasks one at a time, which loses the fact that they are
	 * the same job across several systems — and that is exactly what tells a reader whether a
	 * near-duplicate beneath it is a second proposal or the same one again.
	 */
	partOf?: string;
	/** Controlled by the review sheet so its `D` shortcut can reveal this card's inline form. */
	isDismissOpen?: boolean;
	onDismissOpenChange?: (open: boolean) => void;
}

/** A named fact, so review evidence does not collapse into an unlabeled list of grey lines. */
/** Where the card came from, said the way a reader can check it. */
function SourceLine(props: { refs: readonly SourceRefOut[]; onSelectSource?: SelectSource }) {
	const { refs, onSelectSource } = props;

	if (!refs.length) {
		/**
		 * A card nothing was read for.
		 *
		 * `system` is Bundle's standard checklist, narrowed by what the property has: a house with a
		 * deck gets the deck items and a house without never sees them. So it is neither "no source"
		 * nor a document — it is guidance that applies here, and the schema calls it a baseline
		 * checklist card.
		 *
		 * Which matters to a reviewer more than it looks: there is no document, so the pipeline cannot
		 * have misread one. The question drops from "did it read this right" to "does this apply to
		 * me", and the line has to say so rather than implying a file exists somewhere.
		 *
		 * The only empty case that reaches here: the caller draws no Source fact at all for a card
		 * whose sources exist but are not this reader's to see.
		 */
		return (
			<HStack gap={1.5} color="fg.muted" align="center">
				{/* Not the file icon. A document glyph beside it read as a document reference whatever
				    the words said, which is most of why the old copy misled. */}
				<Icon as={LuClipboardList} boxSize={4} />
				<Text textStyle="body.sm">Standard guidance, not from a specific document</Text>
			</HStack>
		);
	}

	return (
		<VStack align="stretch" gap={1}>
			{refs.map((ref) => {
				const canOpen = Boolean(onSelectSource && ref.doc_id);
				return (
					<HStack
						key={`${ref.doc_id}-${ref.filename}`}
						gap={1.5}
						align="center"
						color={canOpen ? 'brand.fg' : 'fg.muted'}
						minW={0}
						alignSelf="flex-start"
						maxW="full"
						// The one line on the card that can be checked rather than read, so it is the one
						// that behaves like a link. A source with no record behind it stays plain text:
						// an affordance that opens nothing is worse than none.
						{...(canOpen
							? {
									as: 'button' as const,
									type: 'button' as const,
									cursor: 'pointer',
									borderRadius: 'sm',
									_hover: { textDecoration: 'underline' },
									_focusVisible: {
										outline: '2px solid',
										outlineColor: 'brand.solid',
										outlineOffset: '2px',
									},
									onClick: () => onSelectSource?.(ref.doc_id as number, ref.pages),
								}
							: {})}
					>
						<Icon as={LuFileText} boxSize={4} flexShrink={0} />
						<Text textStyle="body.sm" truncate>
							{ref.filename}
						</Text>
						{ref.pages?.length ? (
							<Text textStyle="body.sm" flexShrink={0}>
								p.{ref.pages.join(', ')}
							</Text>
						) : null}
					</HStack>
				);
			})}
		</VStack>
	);
}

/**
 * One proposal, with what it takes to judge it.
 *
 * A schedule row answers "what is due" and drops everything else. This answers a different question
 * — is this task real, and does it belong to this house — so it shows the fields that question turns
 * on: the document and page it was read from, whether the date was estimated and from what, whether
 * the cadence was inferred rather than stated. On the captured properties those are where a bad
 * proposal shows itself; a title alone reads plausible almost every time.
 *
 * The card is not itself a click target. Its two verdicts are buttons, so a third full-surface
 * affordance underneath them would be a click the reader has to aim away from — the details link
 * carries that instead.
 */
export function ProposalCard(props: ProposalCardProps) {
	const {
		node,
		onKeep,
		cadence,
		onCadenceChange,
		anchorDate,
		onAnchorDateChange,
		onDismiss,
		isPending = false,
		hasFailed = false,
		onOpenDetails,
		hideVerdicts = false,
		isSelected = false,
		hideChildList = false,
		partOf,
		onSelectSource,
		visibleDocumentIds,
		flush = false,
		showDetails = true,
		isDismissOpen: controlledDismissOpen,
		onDismissOpenChange,
	} = props;
	const [internalDismissOpen, setInternalDismissOpen] = useState(false);
	const dismissFormId = useId();
	const isDismissOpen = controlledDismissOpen ?? internalDismissOpen;
	const setDismissOpen = (open: boolean) => {
		setInternalDismissOpen(open);
		onDismissOpenChange?.(open);
	};

	const systemName = node.system?.display_name;
	const systemDetail = node.system?.detail;
	const visibleSourceRefs = visiblePlanSourceRefs(node, visibleDocumentIds);
	// A card whose sources are all withheld draws no Source fact at all. "No source recorded" would
	// be false, and a line saying the source is hidden would disclose by its absence the one thing
	// the filter withholds — so a card with nothing to cite has to be indistinguishable from a card
	// whose citation is not this reader's to see. `system` guidance is a real origin rather than an
	// inaccessible document, so it keeps its line.
	const showSource = node.source_origin === 'system' || visibleSourceRefs.length > 0;
	// `date.basis` is free text the pipeline lifted out of a document — "Producer Statement PS3,
	// 14 March 2024" names the record as surely as the Source fact does. Nothing ties it to a
	// `doc_id`, so on a document-derived card its visibility cannot be established at all: it may
	// name a different record than `source_refs`, and one visible source is no evidence about it.
	// Withheld outright until the API says which document it came from. A card read out of no
	// record — `system` guidance, a household's own task — has nothing to withhold.
	const canShowDateBasis = node.source_origin !== 'document';
	// Work and instructions are different answers to "what is this proposal", so they get a list
	// each. One list mixed a task the user would be agreeing to with a step telling them how.
	const taskTitles = taskChildren(node).map((child) => child.title);
	const stepTitles = procedureChildren(node).map((child) => child.title);
	// A group is rejected by dismissing what it holds, so the button has to say the size of that.
	const dismissLabel =
		dismissActionFor(node) === PlanActions.BulkDismiss ? groupActionLabel('Discard', node) : 'Discard';
	const warnings = node.annotations?.warnings ?? [];
	const confirmNotes = node.annotations?.confirm_notes ?? [];

	return (
		<VStack
			align="stretch"
			gap={3}
			minW={0}
			p={flush ? 0 : 4}
			// The token, rather than the four properties it stands for — which is also how the card gets
			// the radius and shadow every other card in the app has.
			layerStyle={flush ? undefined : 'card'}
			boxShadow="none"
			borderColor={isSelected ? 'brand.solid' : undefined}
			// With its verdicts in the pane the card has no controls of its own, so the whole surface
			// becomes the way to open it — a card carrying nothing to press is otherwise inert.
			{...(hideVerdicts && onOpenDetails
				? {
						as: 'button' as const,
						type: 'button' as const,
						textAlign: 'left' as const,
						cursor: 'pointer',
						onClick: onOpenDetails,
						_hover: { borderColor: 'border.strong' },
						_focusVisible: {
							outline: '2px solid',
							outlineColor: 'brand.solid',
							outlineOffset: '2px',
						},
					}
				: {})}
		>
			<VStack align="stretch" gap={1.5}>
				<HStack
					justify="space-between"
					align={{ base: 'stretch', sm: 'flex-start' }}
					flexDirection={{ base: 'column', sm: 'row' }}
					gap={{ base: 2, sm: 3 }}
				>
					{onOpenDetails && !hideVerdicts ? (
						<chakra.button
							type="button"
							minW={0}
							textAlign="left"
							textStyle="text.xl"
							fontWeight="semibold"
							color="brand.fg"
							cursor="pointer"
							borderRadius="sm"
							_hover={{ textDecoration: 'underline' }}
							_focusVisible={{ outline: '2px solid', outlineColor: 'brand.solid', outlineOffset: '2px' }}
							onClick={onOpenDetails}
						>
							{node.title}
						</chakra.button>
					) : (
						<Text textStyle="text.xl" fontWeight="semibold" minW={0}>
							{node.title}
						</Text>
					)}
					{node.display_category ? (
						<Badge size="sm" variant="subtle" flexShrink={0} alignSelf="flex-start">
							{planTypeLabel(node.display_category)}
						</Badge>
					) : null}
				</HStack>

				{partOf ? (
					<HStack
						gap={1.5}
						alignSelf="flex-start"
						px={2}
						py={0.5}
						borderRadius="full"
						bg="surface.subtle"
						color="brand.fg"
						minW={0}
					>
						<Icon as={LuListChecks} boxSize={3} flexShrink={0} />
						<Text textStyle="micro.strong" truncate>
							Part of {partOf}
						</Text>
					</HStack>
				) : null}

				{node.description ? (
					<Text textStyle="text.md" color="fg.muted">
						{node.description}
					</Text>
				) : null}
			</VStack>

			{/* Ruled, as the contact review's fields are. The facts are label/value pairs of very
			    different heights — a schedule runs to three lines where a system runs to one — and on
			    gap alone the taller ones read as paragraphs rather than as rows of one table.

			    Chakra's own separator rather than a border per row: the facts are conditional siblings
			    with no index between them, and `getValidChildren` drops the absent ones so no rule is
			    drawn against nothing. */}
			<VStack
				hidden={!showDetails}
				align="stretch"
				gap={2.5}
				separator={<StackSeparator borderColor="border.default" />}
			>
				{systemName ? (
					<ReviewFact label="For">
						<Text textStyle="body.sm" color="fg.muted">
							{systemName}
							{systemDetail && systemDetail !== systemName ? ` — ${systemDetail}` : ''}
						</Text>
					</ReviewFact>
				) : null}

				<ProposalFrequency
					node={node}
					canShowDateBasis={canShowDateBasis}
					cadence={cadence}
					onCadenceChange={onCadenceChange}
					anchorDate={anchorDate}
					onAnchorDateChange={onAnchorDateChange}
				/>

				{showSource ? (
					<ReviewFact label="Source">
						<SourceLine refs={visibleSourceRefs} onSelectSource={onSelectSource} />
					</ReviewFact>
				) : null}

				{/* Keyed on having sub-tasks rather than on being a group: a card can carry them without
				    being one, and gating on the node type hid five of them behind a parent that simply
				    was not typed as a group. The count comes from `child_count` because the read sends
				    it whether or not it nests the children themselves — so a card says how many it has
				    even when it cannot say what they are. */}
				{!hideChildList && taskTitles.length ? (
					<VStack align="stretch" gap={1.5} p={3} bg="surface.subtle" borderRadius="l2">
						<HStack gap={1.5} color="fg.muted">
							<Icon as={LuListChecks} boxSize={4} flexShrink={0} />
							<Text textStyle="body.sm" fontWeight="semibold">
								Includes {taskTitles.length} sub-task{taskTitles.length === 1 ? '' : 's'}
							</Text>
						</HStack>
						{
							<Grid
								as="ul"
								templateColumns={{ base: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }}
								columnGap={6}
								rowGap={1}
								ps={5}
								color="fg.muted"
							>
								{taskTitles.map((title, index) => (
									<Text as="li" textStyle="body.sm" key={`${index}-${title}`}>
										{title}
									</Text>
								))}
							</Grid>
						}
					</VStack>
				) : null}

				{/* The read sends `child_count` whether or not it nests the children. With none to read, the
				    count is all there is to say — and naming a kind here would be a guess. */}
				{!withChildren(node) && subtaskCount(node) > 0 ? (
					<VStack align="stretch" gap={1.5} p={3} bg="surface.subtle" borderRadius="l2">
						<HStack gap={1.5} color="fg.muted">
							<Icon as={LuListChecks} boxSize={4} flexShrink={0} />
							<Text textStyle="body.sm" fontWeight="semibold">
								Includes {subtaskCount(node)} item{subtaskCount(node) === 1 ? '' : 's'}
							</Text>
						</HStack>
					</VStack>
				) : null}

				{/* Its own container: steps say how the work is done, not what is being proposed. */}
				{stepTitles.length ? (
					<VStack align="stretch" gap={1.5} p={3} bg="surface.subtle" borderRadius="l2">
						<HStack gap={1.5} color="fg.muted">
							<Icon as={LuClipboardList} boxSize={4} flexShrink={0} />
							<Text textStyle="body.sm" fontWeight="semibold">
								Includes {stepTitles.length} step{stepTitles.length === 1 ? '' : 's'}
							</Text>
						</HStack>
						<Grid
							as="ol"
							templateColumns={{ base: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }}
							columnGap={6}
							rowGap={1}
							ps={5}
							color="fg.muted"
						>
							{stepTitles.map((title, index) => (
								<Text as="li" textStyle="body.sm" key={`${index}-${title}`}>
									{title}
								</Text>
							))}
						</Grid>
					</VStack>
				) : null}

				{warnings.map((warning) => (
					<HStack key={warning.issue_id ?? warning.title} gap={1.5} color="orange.600" align="flex-start">
						<Icon as={LuTriangleAlert} boxSize={4} mt={0.5} flexShrink={0} />
						{/* The one row on the card that should stop a reader mid-scan. Orange at the same
						    size as everything else did not — it was another grey line that happened not
						    to be grey. */}
						<Text textStyle="body.sm" fontWeight="semibold">
							{warning.title ?? warning.issue_kind}
						</Text>
					</HStack>
				))}

				{confirmNotes.map((note) => (
					<HStack key={note} gap={1.5} color="fg.subtle" align="flex-start">
						<Icon as={LuInfo} boxSize={4} mt={0.5} flexShrink={0} />
						<Text textStyle="body.sm">{note}</Text>
					</HStack>
				))}
			</VStack>

			{hasFailed ? (
				<Text textStyle="body.sm" color="red.600">
					That did not save. Press the button again to retry.
				</Text>
			) : null}

			{hideVerdicts ? null : (
				<>
					<Separator borderColor="border.default" />
					<VStack align="stretch" gap={3}>
						{/* The three verdicts are one choice, and the buttons name only the verbs. */}
						<Text textStyle="micro" color="fg.subtle">
							{canElectToSchedule(node) ? VERDICT_GUIDANCE : UNDATED_VERDICT_GUIDANCE}
						</Text>
						<Stack
							direction={{ base: 'column', sm: 'row' }}
							align={{ base: 'stretch', sm: 'center' }}
							gap={3}
							justify="space-between"
							minW={0}
						>
							{onOpenDetails ? (
								<chakra.button
									type="button"
									textStyle="body.sm"
									color="brand.fg"
									cursor="pointer"
									borderRadius="sm"
									_hover={{ textDecoration: 'underline' }}
									_focusVisible={{
										outline: '2px solid',
										outlineColor: 'brand.solid',
										outlineOffset: '2px',
									}}
									onClick={onOpenDetails}
								>
									Details
								</chakra.button>
							) : null}
							<ProposalVerdictActions
								node={node}
								onKeep={onKeep}
								onDismiss={onDismiss}
								dismissLabel={dismissLabel}
								isDismissOpen={isDismissOpen}
								dismissFormId={dismissFormId}
								onDismissOpenChange={setDismissOpen}
								isPending={isPending}
							/>
						</Stack>
						{/* Withdrawn while a verdict is in flight: the card's own answer is already on its
						    way, and a form still offering the opposite one invites a contradiction the
						    queue would only have to refuse. */}
						{onDismiss && isDismissOpen && !isPending ? (
							<DismissReasonForm
								vocabulary={PLAN_DISMISS_VOCABULARY}
								id={dismissFormId}
								onCancel={() => setDismissOpen(false)}
								onDismiss={(detail) => {
									setDismissOpen(false);
									onDismiss(detail);
								}}
							/>
						) : null}
					</VStack>
				</>
			)}
		</VStack>
	);
}

export default ProposalCard;
